import type { DetailsPatch, DetailsValues } from '../state/detailsValues';

/**
 * Edit mode in the shared card (brief §3; a modal since decision 16), as a pure
 * reducer so every rule is assertable: one card open at a time; Cancel and Esc
 * close keeping nothing; a click outside the modal with unsaved changes asks
 * first, without changes it just closes; Save writes and says "✓ Saved".
 */
export const CARD_IDS = ['about', 'people', 'logTime', 'when', 'belongs'] as const;
export type CardId = (typeof CARD_IDS)[number];

/** The values each card edits — its draft holds these, and only these. */
export const CARD_FIELDS: Record<CardId, readonly (keyof DetailsValues)[]> = {
  about: ['description', 'files', 'links'],
  people: ['projectManagerId', 'accountManagerId', 'partnerId', 'contactId'],
  logTime: ['logTimeScope', 'askForEtc'],
  when: ['startDate', 'endDate', 'deliveryDate', 'includeInResourcePlanning', 'likelihoodPct'],
  belongs: ['legalEntityId', 'departmentId', 'projectType', 'projectCategory'],
};

export type CardEditorState = {
  /** The card in edit mode, if any — never more than one. */
  editing: CardId | null;
  /** The editing card's fields as they stand in its form. */
  draft: DetailsPatch;
  /** A click outside the modal with unsaved changes: the unsaved-changes dialog is up. */
  confirmClose: boolean;
  /** The card that just saved, for "✓ Saved" — cleared after two seconds. */
  saved: CardId | null;
};

export const INITIAL_CARD_EDITOR: CardEditorState = {
  editing: null,
  draft: {},
  confirmClose: false,
  saved: null,
};

const pick = (values: DetailsValues, card: CardId): DetailsPatch => {
  const draft: Record<string, unknown> = {};
  for (const field of CARD_FIELDS[card]) draft[field] = values[field];
  return structuredClone(draft) as DetailsPatch;
};

/** Whether the editing card's form differs from what is saved. */
export const isDirty = (state: CardEditorState, values: DetailsValues): boolean =>
  state.editing !== null &&
  JSON.stringify(state.draft) !== JSON.stringify(pick(values, state.editing));

export type CardEditorAction =
  /** ✎, or a click on the card: open it, unless one is already open. */
  | { type: 'request'; card: CardId; values: DetailsValues }
  | { type: 'change'; patch: DetailsPatch }
  /** Cancel, or Esc: leave edit mode, keeping nothing. */
  | { type: 'cancel' }
  /** The draft has been written; the card shows "✓ Saved". */
  | { type: 'saved' }
  | { type: 'savedExpired' }
  /** A click outside the modal: close it, or ask first if it has unsaved changes. */
  | { type: 'requestClose'; values: DetailsValues }
  /** The unsaved-changes dialog: Keep editing. */
  | { type: 'keepEditing' };

const startEditing = (
  state: CardEditorState,
  card: CardId,
  values: DetailsValues,
): CardEditorState => ({
  ...state,
  editing: card,
  draft: pick(values, card),
  confirmClose: false,
  saved: null,
});

export const cardEditorReducer = (
  state: CardEditorState,
  action: CardEditorAction,
): CardEditorState => {
  switch (action.type) {
    case 'request':
      if (state.editing !== null) return state;
      return startEditing(state, action.card, action.values);
    case 'change':
      return state.editing === null
        ? state
        : { ...state, draft: { ...state.draft, ...action.patch } };
    case 'cancel':
      return { ...state, editing: null, draft: {}, confirmClose: false };
    case 'saved':
      return { ...state, saved: state.editing, editing: null, draft: {}, confirmClose: false };
    case 'savedExpired':
      return { ...state, saved: null };
    case 'requestClose':
      if (state.editing === null) return state;
      return isDirty(state, action.values)
        ? { ...state, confirmClose: true }
        : { ...state, editing: null, draft: {}, confirmClose: false };
    case 'keepEditing':
      return { ...state, confirmClose: false };
  }
};
